import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'

/**
 * LEEVIEW - raw per-well daily oil table.
 *
 * Var columns compare Oil Today against two different baselines, on purpose:
 *   Fcst Var  = vs the allocated ComboCurve forecast (is the curve right?)
 *   L7D/L30D  = vs the well's OWN recent average     (is the well off its baseline?)
 *
 * Columns auto-size to their widest actual value so nothing ever truncates, and the
 * text columns absorb the leftover width so the table does not scroll sideways.
 */

// Every column, in display order. `x: true` = hidden unless "Extra columns" is on.
const COLUMNS = [
    { k: 'well', label: 'Well Name', txt: true },
    { k: 'lease', label: 'Lease', txt: true },
    { k: 'cls', label: 'Class', txt: true, x: true },
    { k: 'route', label: 'Route', txt: true, x: true },
    { k: 'oilToday', label: 'Oil Today', sep: true },
    { k: 'fcToday', label: 'Forecast', sep: true },
    { k: 'varFc', label: 'Fcst Var', varc: true },
    { k: 'varFcPct', label: 'Fcst %', varc: true, pct: true },
    { k: 'oil7', label: 'Oil L7D', sep: true },
    { k: 'var7', label: 'L7D Var', varc: true },
    { k: 'var7Pct', label: 'L7D %', varc: true, pct: true },
    { k: 'oil30', label: 'Oil L30D', sep: true },
    { k: 'var30', label: 'L30D Var', varc: true },
    { k: 'var30Pct', label: 'L30D %', varc: true, pct: true },
    { k: 'dt7', label: 'DT Hrs', sep: true },
    { k: 'dtToday', label: 'DT Today', x: true },
    { k: 'dt30', label: 'DT L30D', x: true },
    { k: 'gasToday', label: 'Gas Today', sep: true, x: true },
    { k: 'waterToday', label: 'Water Today', x: true },
    { k: 'nri', label: 'NRI', x: true, nosum: true },
    { k: 'lastOil', label: 'Last Oil', txt: true, x: true },
    { k: 'notes', label: 'Notes', txt: true, sep: true }
]

const RANKS = [
    { v: 'var7Pct', label: 'L7D Var % (today vs L7D avg)' },
    { v: 'var30Pct', label: 'L30D Var % (today vs L30D avg)' },
    { v: 'varFcPct', label: 'Forecast Var % (today vs forecast)' },
    { v: 'var7', label: 'L7D Var (bbl)' },
    { v: 'var30', label: 'L30D Var (bbl)' },
    { v: 'varFc', label: 'Forecast Var (bbl)' },
    { v: 'oilToday', label: 'Oil Today' },
    { v: 'oil30', label: 'Oil L30D avg' }
]

// Text columns share leftover width in these proportions.
const FLEX = { well: 2, lease: 1.4, notes: 4 }
const MINW = { well: 110, lease: 80, notes: 120 }
const PAD = 12

// Well name and Lease drill through to Well Analysis, which selects by
// ComboCurveTie (?combo=) and, optionally, WolfpakWellName (?well=). LEEVIEW's
// `well` IS the WolfpakWellName - both sides join Wells.UnitName = WolfpakWellName.
// Wells with no Wolfpak_Peloton tie have no combo, so they stay plain text.
const drillTo = (row, k) => {
    if (!row.combo) return null
    const combo = `/well-analysis?combo=${encodeURIComponent(row.combo)}`
    if (k === 'lease') return combo
    if (k === 'well') return `${combo}&well=${encodeURIComponent(row.well)}`
    return null
}

const byKey = Object.fromEntries(COLUMNS.map(c => [c.k, c]))
const isNum = k => !byKey[k]?.txt
const fmt = (v, k) => {
    if (v === null || v === undefined || v === '') return ''
    if (!isNum(k)) return v
    if (k === 'nri') return v ? Number(v).toFixed(4) : ''
    if (byKey[k]?.pct) return Number(v).toFixed(1) + '%'
    return Number(v).toFixed(2)
}

function LeeView() {
    const { theme } = useTheme()
    const dark = theme === 'dark'

    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const [rank, setRank] = useState('oil30')
    const [dir, setDir] = useState(-1)
    const [q, setQ] = useState('')
    const [cls, setCls] = useState('')
    const [route, setRoute] = useState('')
    const [onlyVar, setOnlyVar] = useState(false)
    const [onlyOil, setOnlyOil] = useState(false)
    const [extra, setExtra] = useState(false)

    const wrapRef = useRef(null)
    const [widths, setWidths] = useState({})
    const [tooWide, setTooWide] = useState(false)

    useEffect(() => {
        let alive = true
        ;(async () => {
            try {
                const res = await fetch('/api/leeview')
                if (!res.ok) throw new Error(`API returned ${res.status}`)
                const json = await res.json()
                if (alive) setData(json)
            } catch (e) {
                if (alive) setError(e.message)
            } finally {
                if (alive) setLoading(false)
            }
        })()
        return () => { alive = false }
    }, [])

    const wells = data?.wells || []
    const cols = useMemo(() => COLUMNS.filter(c => extra || !c.x), [extra])

    const classOpts = useMemo(
        () => [...new Set(wells.map(w => w.cls).filter(Boolean))].sort(), [wells])
    const routeOpts = useMemo(
        () => [...new Set(wells.map(w => w.route).filter(Boolean))].sort(), [wells])

    const view = useMemo(() => {
        const needle = q.trim().toLowerCase()
        const out = wells.filter(r => {
            if (cls && r.cls !== cls) return false
            if (route && r.route !== route) return false
            if (onlyOil && !(r.oilToday > 0 || r.oil30 > 0)) return false
            if (onlyVar) {
                const p = byKey[rank]?.pct ? r[rank] : (r[rank + 'Pct'] ?? r[rank])
                if (!(p !== null && p !== undefined && Math.abs(p) > 20)) return false
            }
            if (needle) {
                const hay = `${r.well} ${r.lease} ${r.county} ${r.notes}`.toLowerCase()
                if (!hay.includes(needle)) return false
            }
            return true
        })
        out.sort((a, b) => {
            const x = a[rank], y = b[rank]
            // nulls (undefined % on a zero baseline) always sink to the bottom
            const xn = x === null || x === undefined, yn = y === null || y === undefined
            if (xn && yn) return 0
            if (xn) return 1
            if (yn) return -1
            return (typeof x === 'number' ? x - y : String(x).localeCompare(String(y))) * dir
        })
        return out
    }, [wells, q, cls, route, onlyVar, onlyOil, rank, dir])

    const totals = useMemo(() => {
        const t = {}
        for (const c of cols) {
            if (!isNum(c.k) || c.nosum || c.pct) continue
            t[c.k] = view.reduce((s, r) => s + (r[c.k] || 0), 0)
        }
        return t
    }, [view, cols])

    // Auto-size columns: measure the widest real value (header, cells, totals row)
    // and fit to it; text columns then split whatever width is left over.
    const measure = useCallback(() => {
        if (!wells.length || !wrapRef.current) return
        const canvas = measure._c || (measure._c = document.createElement('canvas'))
        const ctx = canvas.getContext('2d')
        const FAM = 'ui-sans-serif,system-ui,"Segoe UI",Roboto,sans-serif'
        const w = (txt, font) => { ctx.font = font; return ctx.measureText(String(txt)).width }

        const need = {}
        for (const c of cols) {
            // headers render uppercase with letter-spacing
            let px = w(c.label.toUpperCase(), `700 10px ${FAM}`) + c.label.length * 0.3 + PAD
            if (FLEX[c.k]) {
                px = Math.max(px, MINW[c.k])
            } else {
                for (const r of wells) {
                    const v = fmt(r[c.k], c.k)
                    if (v !== '') px = Math.max(px, w(v, `12px ${FAM}`) + PAD)
                }
                // the totals row is bold and wider than any single cell
                if (isNum(c.k) && !c.nosum && !c.pct) {
                    const t = wells.reduce((s, r) => s + (r[c.k] || 0), 0).toFixed(1)
                    px = Math.max(px, w(t, `700 12px ${FAM}`) + PAD)
                }
            }
            need[c.k] = Math.ceil(px)
        }

        const avail = wrapRef.current.clientWidth - 2
        const total = cols.reduce((a, c) => a + need[c.k], 0)
        const slack = avail - total
        const flexKeys = cols.filter(c => FLEX[c.k])
        const growSum = flexKeys.reduce((a, c) => a + FLEX[c.k], 0)
        if (slack > 0 && growSum > 0) {
            for (const c of flexKeys) need[c.k] += Math.floor((slack * FLEX[c.k]) / growSum)
        }
        setTooWide(total > avail)
        setWidths(need)
    }, [wells, cols])

    useLayoutEffect(() => { measure() }, [measure])
    useEffect(() => {
        const onResize = () => measure()
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [measure])

    const downloadCsv = () => {
        // CSV always carries every column, including hidden ones
        const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`
        const lines = [COLUMNS.map(c => esc(c.label)).join(',')]
        for (const r of view) lines.push(COLUMNS.map(c => esc(r[c.k])).join(','))
        const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `LEEVIEW_${data?.anchorDate || 'export'}.csv`
        a.click()
        URL.revokeObjectURL(a.href)
    }

    const clickHeader = k => {
        if (rank === k) setDir(d => -d)
        else { setRank(k); setDir(isNum(k) ? -1 : 1) }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }
    if (error) {
        return (
            <div className="text-center py-12">
                <p className="text-lg font-semibold text-red-400">LEEVIEW failed to load</p>
                <p className={`mt-2 text-sm ${dark ? 'text-gray-400' : 'text-gray-600'}`}>{error}</p>
            </div>
        )
    }

    const bd = dark ? 'border-slate-700' : 'border-gray-200'
    const hd = dark ? 'bg-slate-800' : 'bg-gray-100'
    const panel = dark ? 'bg-slate-900' : 'bg-white'
    const zebra = dark ? 'bg-slate-950/40' : 'bg-gray-50/70'
    const mut = dark ? 'text-gray-400' : 'text-gray-500'
    const ctl = `${dark ? 'bg-slate-800 border-slate-600 text-gray-100' : 'bg-white border-gray-300 text-gray-800'} border rounded px-2 py-1 text-xs`

    const kpis = [
        ['Oil Today', totals.oilToday],
        ['Forecast Today', totals.fcToday],
        ['L7D avg/d', totals.oil7],
        ['L30D avg/d', totals.oil30]
    ]

    return (
        <div className="space-y-4">
            <div className={`border-b-2 ${dark ? 'border-blue-800' : 'border-blue-200'} pb-3`}>
                <h1 className={`text-xl sm:text-3xl font-bold ${dark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                    <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                    LEEVIEW
                </h1>
                <p className={`mt-1 text-xs ${mut}`}>
                    Producing &amp; inactive wells &mdash; raw daily oil. Production date{' '}
                    <b className={dark ? 'text-gray-200' : 'text-gray-700'}>{data?.anchorDate}</b>{' '}
                    (feed lags 2 days) &middot; {data?.wellCount} wells &middot;{' '}
                    {data?.injectorsExcluded} injectors excluded
                </p>
            </div>

            <div className="flex flex-wrap gap-2">
                {kpis.map(([label, v]) => (
                    <div key={label} className={`${panel} border ${bd} rounded-md px-3 py-1.5`}>
                        <div className={`text-[10px] uppercase tracking-wide ${mut}`}>{label}</div>
                        <div className={`text-base font-bold tabular-nums ${dark ? 'text-white' : 'text-gray-900'}`}>
                            {Math.round(v || 0).toLocaleString()}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-2 items-center">
                <span className={`text-xs font-semibold ${dark ? 'text-gray-200' : 'text-gray-700'}`}>Rank by:</span>
                <select value={rank} onChange={e => setRank(e.target.value)}
                    className={`${ctl} ${dark ? 'ring-1 ring-blue-500/60' : 'ring-1 ring-blue-400/60'}`}>
                    {RANKS.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
                </select>
                <select value={dir} onChange={e => setDir(Number(e.target.value))} className={ctl}>
                    <option value={-1}>Best first</option>
                    <option value={1}>Worst first</option>
                </select>
                <input type="text" value={q} onChange={e => setQ(e.target.value)}
                    placeholder="Filter well / lease / county / note..."
                    className={`${ctl} min-w-[200px]`} />
                <select value={cls} onChange={e => setCls(e.target.value)} className={ctl}>
                    <option value="">All classifications</option>
                    {classOpts.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={route} onChange={e => setRoute(e.target.value)} className={ctl}>
                    <option value="">All routes</option>
                    {routeOpts.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <label className={`flex items-center gap-1 text-xs ${mut}`}>
                    <input type="checkbox" checked={onlyVar} onChange={e => setOnlyVar(e.target.checked)} />
                    Only |rank| &gt; 20%
                </label>
                <label className={`flex items-center gap-1 text-xs ${mut}`}>
                    <input type="checkbox" checked={onlyOil} onChange={e => setOnlyOil(e.target.checked)} />
                    Only wells with oil
                </label>
                <label className={`flex items-center gap-1 text-xs ${mut}`}>
                    <input type="checkbox" checked={extra} onChange={e => setExtra(e.target.checked)} />
                    Extra columns
                </label>
                <button onClick={downloadCsv} className={`${ctl} cursor-pointer`}>Download CSV</button>
                <span className={`text-xs ${mut}`}>{view.length} of {wells.length} rows</span>
            </div>

            <div ref={wrapRef}
                className={`border ${bd} rounded-md ${panel} overflow-y-auto`}
                style={{ maxHeight: '72vh', overflowX: tooWide ? 'auto' : 'hidden' }}>
                <table className="w-full tabular-nums"
                    style={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0,
                             minWidth: tooWide ? cols.reduce((a, c) => a + (widths[c.k] || 0), 0) : 0 }}>
                    <thead>
                        <tr>
                            {cols.map(c => (
                                <th key={c.k}
                                    onClick={() => clickHeader(c.k)}
                                    title={c.label}
                                    style={{ width: widths[c.k] }}
                                    className={`sticky top-0 z-20 cursor-pointer select-none px-1 py-1
                                        text-[10px] font-bold uppercase tracking-wide whitespace-nowrap
                                        overflow-hidden text-ellipsis border-b ${bd}
                                        ${c.txt ? 'text-left' : 'text-right'}
                                        ${c.sep ? `border-l ${bd}` : ''}
                                        ${rank === c.k
                                            ? (dark ? 'bg-slate-700 text-blue-300' : 'bg-blue-50 text-blue-700')
                                            : `${hd} ${dark ? 'text-gray-300' : 'text-gray-600'}`}`}>
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {view.map((r, i) => (
                            <tr key={r.well} className={i % 2 ? zebra : ''}>
                                {cols.map(c => {
                                    const v = r[c.k]
                                    let tone = ''
                                    if (c.varc && v !== null && v !== undefined) {
                                        tone = v > 0.005
                                            ? (dark ? 'text-green-400' : 'text-green-700')
                                            : v < -0.005
                                                ? (dark ? 'text-red-400' : 'text-red-700')
                                                : mut
                                    }
                                    const href = drillTo(r, c.k)
                                    return (
                                        <td key={c.k} title={href ? `${v} - open in Well Analysis` : String(v ?? '')}
                                            className={`px-1 py-1 text-[12px] whitespace-nowrap overflow-hidden
                                                text-ellipsis border-b ${bd}
                                                ${c.txt ? 'text-left' : 'text-right'}
                                                ${c.sep ? `border-l ${bd}` : ''}
                                                ${c.k === 'well' && !href ? `font-semibold ${dark ? 'text-white' : 'text-gray-900'}` : ''}
                                                ${c.k === 'notes' ? mut : ''}
                                                ${tone}`}>
                                            {href ? (
                                                <Link to={href}
                                                    className={`hover:underline ${c.k === 'well' ? 'font-semibold ' : ''}${
                                                        dark ? 'text-blue-300 hover:text-blue-200'
                                                             : 'text-blue-700 hover:text-blue-900'}`}>
                                                    {fmt(v, c.k)}
                                                </Link>
                                            ) : fmt(v, c.k)}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                        <tr><td colSpan={cols.length} style={{ height: 26 }}></td></tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            {cols.map(c => (
                                <td key={c.k}
                                    className={`sticky bottom-0 z-20 px-1 py-1 text-[12px] font-bold
                                        whitespace-nowrap border-t-2 ${bd} ${hd}
                                        ${c.txt ? 'text-left' : 'text-right'}
                                        ${c.sep ? `border-l ${bd}` : ''}
                                        ${dark ? 'text-white' : 'text-gray-900'}`}>
                                    {c.k === 'well'
                                        ? `${view.length} wells`
                                        : totals[c.k] !== undefined ? totals[c.k].toFixed(1) : ''}
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div className={`text-[11.5px] leading-relaxed ${mut} max-w-5xl space-y-1`}>
                <p><b className={dark ? 'text-gray-200' : 'text-gray-700'}>Oil Today / L7D / L30D</b> &mdash; gross oil bbl.
                    Today = the production date above; L7D and L30D are <b>daily averages</b> (window total &divide; 7 or &divide; 30),
                    so a well down part of the window averages lower.</p>
                <p><b className={dark ? 'text-gray-200' : 'text-gray-700'}>L7D Var / L30D Var</b> &mdash; Oil Today minus that
                    well&apos;s own L7D / L30D average. A performance read: is the well off its own recent baseline? It says
                    nothing about whether the curve is right.</p>
                <p><b className={dark ? 'text-gray-200' : 'text-gray-700'}>Fcst Var</b> &mdash; Oil Today minus the allocated
                    ComboCurve forecast. The forecast is stored at <b>lease grain</b> (90 curves), never per well: each well&apos;s
                    forecast is the lease curve &times; that well&apos;s share of its lease&apos;s oil over the last 180 days.
                    Shares sum to 1.0 per lease, so lease and field totals tie exactly. Because a well&apos;s forecast tracks its
                    own recent production, treat this as reliable at <b>lease level</b>; per well it is indicative only.</p>
                <p><b className={dark ? 'text-gray-200' : 'text-gray-700'}>Var % on a zero baseline</b> &mdash; blank, and always
                    sorted last. Sort by the bbl column to surface wells coming back on.</p>
                <p><b className={dark ? 'text-gray-200' : 'text-gray-700'}>Injectors excluded</b> &mdash; by two tests, since the
                    label alone is unreliable: <code>WellClassification = &apos;Injecting&apos;</code>, plus any well that took
                    water and made no oil over 180 days (catches wells tagged <code>Inactive</code> but still injecting).
                    No excluded well produced oil, so no barrels leave the table.</p>
            </div>
        </div>
    )
}

export default LeeView
